import React, { useEffect, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import { db } from "../firebaseConfig";
import {
  collection, addDoc, onSnapshot, query, orderBy,
  Timestamp, updateDoc, doc,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ClearIcon from "@mui/icons-material/Clear";
import { agentState } from "../App.js";
import { useRecoilState } from "recoil";

// ─── Global Fonts ─────────────────────────────────────────────────────────────

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Geist:wght@300;400;500;600&family=Geist+Mono:wght@400;500&display=swap');
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const BANKS = ["TIDE", "MONZO", "STARLING", "NATWEST", "ALLIED"];

const BANK_META = {
  TIDE:     { color: "#0369a1", bg: "#e0f2fe", border: "#7dd3fc" },
  MONZO:    { color: "#c2410c", bg: "#fff7ed", border: "#fdba74" },
  STARLING: { color: "#059669", bg: "#ecfdf5", border: "#6ee7b7" },
  NATWEST:  { color: "#7c3aed", bg: "#f5f3ff", border: "#c4b5fd" },
  ALLIED:   { color: "#1d4ed8", bg: "#eff6ff", border: "#93c5fd" },
};

const EMPTY_FORM = {
  agent: "", folderNo: "", amountInput: "", currency: "GBP",
  rate: "", amountGBP: 0, bank: "", beneficiary: "",
  refundedBy: "", reason: "", date: "", remarks: "",
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); max-height: 0; }
  to   { opacity: 1; transform: translateY(0); max-height: 1000px; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalize = (v) => String(v || "").trim().toLowerCase();
const initials  = (n) => (n || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
const fmtDate   = (s) => s && s !== "-" ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtGBP    = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AVATAR_PALETTES = [
  ["#dbeafe", "#1d4ed8"], ["#dcfce7", "#15803d"], ["#fef3c7", "#b45309"],
  ["#fce7f3", "#be185d"], ["#ede9fe", "#6d28d9"], ["#e0f2fe", "#0369a1"],
  ["#ffedd5", "#c2410c"], ["#f0fdf4", "#166534"],
];
const avatarPalette = (name) => AVATAR_PALETTES[(name || "?").charCodeAt(0) % AVATAR_PALETTES.length];

// ─── Sub-components ───────────────────────────────────────────────────────────

const FormField = ({ label, children }) => (
  <FieldWrap>
    <FieldLabel>{label}</FieldLabel>
    {children}
  </FieldWrap>
);

const StyledSelect = ({ name, value, onChange, options, placeholder }) => (
  <InputEl as="select" name={name} value={value} onChange={onChange}>
    <option value="">{placeholder || "— Select —"}</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </InputEl>
);

const StyledInput = ({ name, value, onChange, type = "text", placeholder }) => (
  <InputEl name={name} value={value} onChange={onChange} type={type} placeholder={placeholder} />
);

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, accent }) => (
  <StatCardWrap $accent={accent}>
    <StatLabel>{label}</StatLabel>
    <StatValue $accent={accent}>{value}</StatValue>
    {sub && <StatSub>{sub}</StatSub>}
  </StatCardWrap>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CustomerRefunds() {
  const [salesPerson] = useRecoilState(agentState);
  const agents = salesPerson.map(item => item.id);

  const [refunds,    setRefunds]    = useState([]);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [filters,    setFilters]    = useState({ folderNo: "", agent: "", bank: "", currency: "", fromDate: "", toDate: "" });
  const [editOpen,   setEditOpen]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [addOpen,    setAddOpen]    = useState(false);
  const [search,     setSearch]     = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "dateObj", dir: "desc" });
  const [activeBank, setActiveBank] = useState("ALL");

  // ── Body scroll lock + Escape ───────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = editOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [editOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && editOpen) setEditOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editOpen]);

  // ── Firestore ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, "customerRefunds"), orderBy("timestamp", "desc"));
    return onSnapshot(q, snap => {
      setRefunds(snap.docs.map(d => ({
        id: d.id, ...d.data(),
        dateObj: d.data().timestamp?.toDate?.() ?? new Date(),
      })));
    });
  }, []);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const calcGBP = (data) =>
    data.currency === "GBP"
      ? parseFloat(data.amountInput || 0)
      : parseFloat(data.amountInput || 0) / parseFloat(data.rate || 1);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleFilter = e => setFilters(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleAdd = async () => {
    if (!form.amountInput || !form.bank) return alert("Amount and bank are required");
    const amountGBP = calcGBP(form);
    try {
      await addDoc(collection(db, "customerRefunds"), {
        ...form, amountGBP, timestamp: Timestamp.now(),
      });
      setForm(EMPTY_FORM);
      setAddOpen(false);
    } catch { alert("Failed to add refund"); }
  };

  const handleEdit = (r) => {
    setEditId(r.id);
    setForm({ ...r, amountInput: r.currency === "GBP" ? r.amountGBP : r.amountInput || "" });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    const amountGBP = calcGBP(form);
    try {
      await updateDoc(doc(db, "customerRefunds", editId), { ...form, amountGBP });
      setEditOpen(false);
    } catch { alert("Failed to update refund"); }
  };

  // ── Sort ────────────────────────────────────────────────────────────────────
  const handleSort = key =>
    setSortConfig(p => ({ key, dir: p.key === key && p.dir === "asc" ? "desc" : "asc" }));

  // ── Filter + Sort ───────────────────────────────────────────────────────────
  const filteredEntries = useMemo(() => {
    let data = [...refunds];

    if (activeBank !== "ALL")   data = data.filter(r => r.bank === activeBank);
    if (filters.folderNo)       data = data.filter(r => normalize(r.folderNo) === normalize(filters.folderNo));
    if (filters.agent)          data = data.filter(r => r.agent === filters.agent);
    if (filters.bank)           data = data.filter(r => r.bank === filters.bank);
    if (filters.currency)       data = data.filter(r => r.currency === filters.currency);
    if (filters.fromDate)       data = data.filter(r => r.dateObj >= new Date(filters.fromDate));
    if (filters.toDate)         data = data.filter(r => r.dateObj <= new Date(filters.toDate));

    if (search) {
      const q = normalize(search);
      data = data.filter(r =>
        normalize(r.folderNo).includes(q)    || normalize(r.agent).includes(q)       ||
        normalize(r.bank).includes(q)        || normalize(r.beneficiary).includes(q) ||
        normalize(r.refundedBy).includes(q)  || normalize(r.reason).includes(q)      ||
        normalize(r.remarks).includes(q)
      );
    }

    data.sort((a, b) => {
      if (sortConfig.key === "amountGBP") {
        const av = Number(a.amountGBP || 0), bv = Number(b.amountGBP || 0);
        return sortConfig.dir === "asc" ? av - bv : bv - av;
      }
      if (sortConfig.key === "dateObj") {
        return sortConfig.dir === "asc" ? a.dateObj - b.dateObj : b.dateObj - a.dateObj;
      }
      const av = String(a[sortConfig.key] || "").toLowerCase();
      const bv = String(b[sortConfig.key] || "").toLowerCase();
      return sortConfig.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });

    return data;
  }, [refunds, filters, search, activeBank, sortConfig]);

  // ── Stats ────────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:    filteredEntries.reduce((s, r) => s + Number(r.amountGBP || 0), 0),
    count:    filteredEntries.length,
    pkr:      filteredEntries.filter(r => r.currency === "PKR").length,
    banks:    new Set(filteredEntries.map(r => r.bank).filter(Boolean)).size,
  }), [filteredEntries]);

  const hasFilters = filters.folderNo || filters.agent || filters.bank ||
    filters.currency || filters.fromDate || filters.toDate || search || activeBank !== "ALL";
  const clearFilters = () => { setFilters({ folderNo: "", agent: "", bank: "", currency: "", fromDate: "", toDate: "" }); setSearch(""); setActiveBank("ALL"); };

  const SortIcon = ({ col }) => (
    <SortIndicator $active={sortConfig.key === col}>
      {sortConfig.key === col ? (sortConfig.dir === "asc" ? "↑" : "↓") : "↕"}
    </SortIndicator>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <GlobalStyle />
      <Page>

        {/* ── Header ── */}
        <Header>
          <HeaderContent>
            <HeaderEyebrow>Financial Operations</HeaderEyebrow>
            <HeaderTitle>Customer<TitleAccent> Refunds</TitleAccent></HeaderTitle>
            <HeaderSub>Track, manage, and reconcile all customer refund transactions</HeaderSub>
          </HeaderContent>
          <HeaderActions>
            <ExportBtn onClick={() => {
              if (!filteredEntries.length) return alert("No data to export");
              const ws = XLSX.utils.json_to_sheet(filteredEntries.map(r => ({
                Agent: r.agent, "Folder #": r.folderNo,
                "Amount (GBP)": Number(r.amountGBP || 0).toFixed(2),
                "Amount (PKR)": r.currency === "PKR" ? r.amountInput : "",
                Currency: r.currency, Bank: r.bank,
                Beneficiary: r.beneficiary, "Refunded By": r.refundedBy,
                Reason: r.reason, Date: r.date, Remarks: r.remarks,
              })));
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, "Refunds");
              XLSX.writeFile(wb, `CustomerRefunds_${new Date().toISOString().slice(0, 10)}.xlsx`);
            }}>
              <BtnIcon>↓</BtnIcon>
              Export Excel
            </ExportBtn>
            <AddBtn onClick={() => setAddOpen(p => !p)}>
              <BtnIcon>{addOpen ? "×" : "+"}</BtnIcon>
              {addOpen ? "Cancel" : "New Refund"}
            </AddBtn>
          </HeaderActions>
        </Header>

        {/* ── Stat Cards ── */}
        <StatsGrid>
          <StatCard label="Total Refunded" value={fmtGBP(stats.total)}       sub={`${stats.count} refunds`} accent />
          <StatCard label="PKR Refunds"    value={stats.pkr}                  sub="in Pakistani Rupee" />
          <StatCard label="Active Banks"   value={stats.banks}                sub="banks used" />
          <StatCard label="Showing"        value={filteredEntries.length}     sub={`of ${refunds.length} entries`} />
        </StatsGrid>

        {/* ── Add Entry Panel ── */}
        {addOpen && (
          <Panel>
            <PanelHeader>
              <PanelTitle>
                <PanelDot $color="#dc2626" />
                New Customer Refund
              </PanelTitle>
            </PanelHeader>
            <PanelBody>
              <FormGrid>
                <FormField label="Agent">
                  <StyledSelect name="agent" value={form.agent} onChange={handleChange} options={agents} />
                </FormField>
                <FormField label="Folder No">
                  <StyledInput name="folderNo" value={form.folderNo} onChange={handleChange} placeholder="e.g. 10452" />
                </FormField>
                <FormField label="Amount">
                  <StyledInput name="amountInput" value={form.amountInput} onChange={handleChange} type="number" placeholder="0.00" />
                </FormField>
                <FormField label="Currency">
                  <StyledSelect name="currency" value={form.currency} onChange={handleChange} options={["GBP", "PKR"]} />
                </FormField>
                {form.currency === "PKR" && (
                  <FormField label="PKR → GBP Rate">
                    <StyledInput name="rate" value={form.rate} onChange={handleChange} type="number" placeholder="e.g. 350" />
                  </FormField>
                )}
                <FormField label="Bank">
                  <StyledSelect name="bank" value={form.bank} onChange={handleChange} options={BANKS} />
                </FormField>
                <FormField label="Beneficiary">
                  <StyledInput name="beneficiary" value={form.beneficiary} onChange={handleChange} placeholder="Recipient name" />
                </FormField>
                <FormField label="Refunded By">
                  <StyledInput name="refundedBy" value={form.refundedBy} onChange={handleChange} placeholder="Staff name" />
                </FormField>
                <FormField label="Reason">
                  <StyledInput name="reason" value={form.reason} onChange={handleChange} placeholder="Reason for refund" />
                </FormField>
                <FormField label="Date">
                  <StyledInput name="date" value={form.date} onChange={handleChange} type="date" />
                </FormField>
                <FormField label="Remarks">
                  <StyledInput name="remarks" value={form.remarks} onChange={handleChange} placeholder="Optional notes…" />
                </FormField>
              </FormGrid>
              <PanelFooter>
                <SubmitBtn onClick={handleAdd}>Add Refund →</SubmitBtn>
              </PanelFooter>
            </PanelBody>
          </Panel>
        )}

        {/* ── Filter + Search Bar ── */}
        <FilterBar>
          <SearchWrap>
            <SearchIconEl>⌕</SearchIconEl>
            <SearchInput
              placeholder="Search refunds — folder, agent, bank, beneficiary, reason…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </SearchWrap>
          <FilterControls>
            <FilterGroup>
              <FilterLabel>Agent</FilterLabel>
              <FilterSelect name="agent" value={filters.agent} onChange={handleFilter}>
                <option value="">All agents</option>
                {agents.map(a => <option key={a} value={a}>{a}</option>)}
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Bank</FilterLabel>
              <FilterSelect name="bank" value={filters.bank} onChange={handleFilter}>
                <option value="">All banks</option>
                {BANKS.map(b => <option key={b} value={b}>{b}</option>)}
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Currency</FilterLabel>
              <FilterSelect name="currency" value={filters.currency} onChange={handleFilter}>
                <option value="">All</option>
                <option value="GBP">GBP</option>
                <option value="PKR">PKR</option>
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Folder No</FilterLabel>
              <FilterInput name="folderNo" value={filters.folderNo} onChange={handleFilter} placeholder="Search…" />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>From Date</FilterLabel>
              <FilterInput type="date" name="fromDate" value={filters.fromDate} onChange={handleFilter} />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>To Date</FilterLabel>
              <FilterInput type="date" name="toDate" value={filters.toDate} onChange={handleFilter} />
            </FilterGroup>
            {hasFilters && (
              <ClearAllBtn onClick={clearFilters}>✕ Clear all</ClearAllBtn>
            )}
          </FilterControls>
        </FilterBar>

        {/* ── Bank Tabs ── */}
        <TypeTabs>
          {["ALL", ...BANKS].map(b => {
            const count = b === "ALL" ? refunds.length : refunds.filter(r => r.bank === b).length;
            const meta  = BANK_META[b];
            return (
              <TypeTab key={b} $active={activeBank === b} $meta={meta} onClick={() => setActiveBank(b)}>
                {b === "ALL" ? "All Banks" : b}
                <TypeCount $active={activeBank === b}>{count}</TypeCount>
              </TypeTab>
            );
          })}
        </TypeTabs>

        {/* ── Table ── */}
        <TableCard>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: 44 }}>#</Th>
                  <Th sortable onClick={() => handleSort("agent")}>Agent <SortIcon col="agent" /></Th>
                  <Th sortable onClick={() => handleSort("folderNo")}>Folder <SortIcon col="folderNo" /></Th>
                  <Th sortable onClick={() => handleSort("amountGBP")}>Amount (GBP) <SortIcon col="amountGBP" /></Th>
                  <Th>PKR Amount</Th>
                  <Th sortable onClick={() => handleSort("bank")}>Bank <SortIcon col="bank" /></Th>
                  <Th>Beneficiary</Th>
                  <Th>Refunded By</Th>
                  <Th>Reason</Th>
                  <Th sortable onClick={() => handleSort("dateObj")}>Date <SortIcon col="dateObj" /></Th>
                  <Th>Remarks</Th>
                  <Th style={{ width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {filteredEntries.length === 0 && (
                  <tr>
                    <td colSpan={12}>
                      <EmptyState>
                        <EmptyIcon>↩</EmptyIcon>
                        <EmptyTitle>No refunds found</EmptyTitle>
                        <EmptyDesc>
                          {hasFilters ? "Try adjusting or clearing your filters" : "Add your first customer refund to get started"}
                        </EmptyDesc>
                      </EmptyState>
                    </td>
                  </tr>
                )}
                {filteredEntries.map((r, idx) => {
                  const [avatarBg, avatarFg] = avatarPalette(r.agent || "?");
                  const bankMeta = BANK_META[r.bank] || { color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" };
                  return (
                    <TableRow key={r.id} $idx={idx}>
                      <Td $muted>{idx + 1}</Td>
                      <Td>
                        <AgentCell>
                          <Avatar style={{ background: avatarBg, color: avatarFg }}>
                            {initials(r.agent)}
                          </Avatar>
                          <AgentName>{r.agent || "—"}</AgentName>
                        </AgentCell>
                      </Td>
                      <Td
                      
                        onClick={() => {
      const url = `/FolderLedger?FolderNumber=${String(r.folderNo)}`;
      window.open(url, "_blank");
    }}
    style={{ cursor: "pointer" }}>
                        <FolderTag>#{r.folderNo || "—"}</FolderTag>
                      </Td>
                      <Td>
                        <AmountGBP>{fmtGBP(r.amountGBP)}</AmountGBP>
                      </Td>
                      <Td $mono $muted>
                        {r.currency === "PKR" ? `₨${Number(r.amountInput || 0).toLocaleString()}` : "—"}
                      </Td>
                      <Td>
                        <BankBadge $color={bankMeta.color} $bg={bankMeta.bg} $border={bankMeta.border}>
                          {r.bank || "—"}
                        </BankBadge>
                      </Td>
                      <Td $muted>{r.beneficiary || "—"}</Td>
                      <Td $muted>{r.refundedBy  || "—"}</Td>
                      <Td>
                        {r.reason
                          ? <ReasonBadge>{r.reason}</ReasonBadge>
                          : <span style={{ color: "#cbd5e1" }}>—</span>}
                      </Td>
                      <Td $mono $muted>{fmtDate(r.date)}</Td>
                      <Td>
                        <RemarksText title={r.remarks}>{r.remarks || "—"}</RemarksText>
                      </Td>
                      <Td>
                        <EditBtn onClick={() => handleEdit(r)} title="Edit entry">
                          <ModeEditIcon style={{ fontSize: 14 }} />
                        </EditBtn>
                      </Td>
                    </TableRow>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>

          <TableFooter>
            <FooterLeft>
              Showing <FooterStrong>{filteredEntries.length}</FooterStrong> of <FooterStrong>{refunds.length}</FooterStrong> entries
            </FooterLeft>
            <FooterRight>
              Total refunded: <FooterTotal>{fmtGBP(stats.total)}</FooterTotal>
            </FooterRight>
          </TableFooter>
        </TableCard>

        {/* ── Edit Modal — portal to document.body ── */}
        {editOpen && ReactDOM.createPortal(
          <Overlay onClick={() => setEditOpen(false)}>
            <Modal onClick={e => e.stopPropagation()}>
              <ModalHead>
                <div>
                  <ModalEyebrow>Customer Refund</ModalEyebrow>
                  <ModalTitle>Edit Record</ModalTitle>
                </div>
                <CloseBtn onClick={() => setEditOpen(false)}>
                  <ClearIcon style={{ fontSize: 18 }} />
                </CloseBtn>
              </ModalHead>

              <ModalBody>
                <FormGrid>
                  <FormField label="Agent">
                    <StyledSelect name="agent" value={agents.find(a => normalize(a) === normalize(form.agent)) || ""} onChange={handleChange} options={agents} />
                  </FormField>
                  <FormField label="Folder No">
                    <StyledInput name="folderNo" value={form.folderNo} onChange={handleChange} />
                  </FormField>
                  <FormField label="Amount">
                    <StyledInput name="amountInput" value={form.amountInput} onChange={handleChange} type="number" />
                  </FormField>
                  <FormField label="Currency">
                    <StyledSelect name="currency" value={form.currency} onChange={handleChange} options={["GBP", "PKR"]} />
                  </FormField>
                  {form.currency === "PKR" && (
                    <FormField label="PKR → GBP Rate">
                      <StyledInput name="rate" value={form.rate} onChange={handleChange} type="number" />
                    </FormField>
                  )}
                  <FormField label="Bank">
                    <StyledSelect name="bank" value={form.bank} onChange={handleChange} options={BANKS} />
                  </FormField>
                  <FormField label="Beneficiary">
                    <StyledInput name="beneficiary" value={form.beneficiary} onChange={handleChange} />
                  </FormField>
                  <FormField label="Refunded By">
                    <StyledInput name="refundedBy" value={form.refundedBy} onChange={handleChange} />
                  </FormField>
                  <FormField label="Reason">
                    <StyledInput name="reason" value={form.reason} onChange={handleChange} />
                  </FormField>
                  <FormField label="Date">
                    <StyledInput name="date" value={form.date} onChange={handleChange} type="date" />
                  </FormField>
                  <FormField label="Remarks">
                    <StyledInput name="remarks" value={form.remarks} onChange={handleChange} />
                  </FormField>
                </FormGrid>

                <NoCodeNote>
                  ℹ️ No authorization code required for refund edits. Review your changes carefully before saving.
                </NoCodeNote>
              </ModalBody>

              <ModalFooter>
                <CancelModalBtn onClick={() => setEditOpen(false)}>
                  Cancel
                </CancelModalBtn>
                <UpdateBtn onClick={handleUpdate}>
                  Save Changes →
                </UpdateBtn>
              </ModalFooter>
            </Modal>
          </Overlay>,
          document.body
        )}

      </Page>
    </>
  );
}

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 36px 32px 80px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'Geist', sans-serif;
  animation: ${fadeUp} 0.4s ease both;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const HeaderContent = styled.div``;

const HeaderEyebrow = styled.div`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
`;

const HeaderTitle = styled.h1`
  font-family: 'Instrument Serif', serif;
  font-size: 36px;
  font-weight: 400;
  color: #0f172a;
  margin: 0 0 6px;
  letter-spacing: -0.5px;
  line-height: 1.1;
`;

const TitleAccent = styled.span`
  font-style: italic;
  color: #dc2626;
`;

const HeaderSub = styled.p`
  font-size: 13.5px;
  color: #64748b;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
`;

const BtnBase = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 500;
  font-family: 'Geist', sans-serif;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
`;

const BtnIcon = styled.span`font-size: 16px; line-height: 1;`;

const ExportBtn = styled(BtnBase)`
  background: white;
  color: #334155;
  border: 1px solid #e2e8f0;
  &:hover { background: #f8fafc; border-color: #cbd5e1; }
`;

const AddBtn = styled(BtnBase)`
  background: #dc2626;
  color: white;
  border: 1px solid transparent;
  &:hover { background: #b91c1c; }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;

const StatCardWrap = styled.div`
  background: ${({ $accent }) => $accent ? "#7f1d1d" : "white"};
  border: 1px solid ${({ $accent }) => $accent ? "transparent" : "#e2e8f0"};
  border-radius: 14px;
  padding: 18px 20px;
  position: relative;
  overflow: hidden;
  ${({ $accent }) => $accent && `
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(220,38,38,0.3) 0%, transparent 60%);
      pointer-events: none;
    }
  `}
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-family: 'Geist Mono', monospace;
  font-size: 22px;
  font-weight: 500;
  color: ${({ $accent }) => $accent ? "white" : "#0f172a"};
  line-height: 1;
  margin-bottom: 4px;
`;

const StatSub = styled.div`font-size: 11.5px; color: #94a3b8;`;

const Panel = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  margin-bottom: 16px;
  overflow: hidden;
  animation: ${slideDown} 0.25s ease both;
`;

const PanelHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: #0f172a;
`;

const PanelDot = styled.div`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const PanelBody = styled.div`padding: 20px;`;

const PanelFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
`;

const FormGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 160px;
  flex: 1;
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #64748b;
`;

const InputEl = styled.input`
  padding: 9px 12px;
  font-size: 13.5px;
  font-family: 'Geist', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.15s;
  appearance: none;
  &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
  &::placeholder { color: #cbd5e1; }
  option { background: white; }
`;

const SubmitBtn = styled.button`
  padding: 10px 24px;
  font-size: 13.5px;
  font-weight: 600;
  font-family: 'Geist', sans-serif;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #b91c1c; }
`;

const FilterBar = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SearchWrap = styled.div`position: relative;`;

const SearchIconEl = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 17px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px 10px 36px;
  font-size: 13.5px;
  font-family: 'Geist', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  transition: all 0.15s;
  &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px rgba(37,99,235,0.08); }
  &::placeholder { color: #cbd5e1; }
`;

const FilterControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  flex: 1;
`;

const FilterLabel = styled.label`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #94a3b8;
`;

const filterBase = `
  padding: 8px 12px;
  font-size: 13px;
  font-family: 'Geist', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.15s;
  &:focus { border-color: #2563eb; background: white; }
  &::placeholder { color: #cbd5e1; }
  option { background: white; }
`;

const FilterSelect = styled.select`
  ${filterBase}
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-color: #f8fafc;
  padding-right: 30px;
`;

const FilterInput = styled.input`${filterBase}`;

const ClearAllBtn = styled.button`
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Geist', sans-serif;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  align-self: flex-end;
  transition: all 0.15s;
  &:hover { background: #fee2e2; }
`;

const TypeTabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const TypeTab = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 500;
  font-family: 'Geist', sans-serif;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  ${({ $active, $meta }) => $active
    ? `
      background: ${$meta ? $meta.bg : "#f1f5f9"};
      color: ${$meta ? $meta.color : "#334155"};
      border: 1.5px solid ${$meta ? $meta.border : "#e2e8f0"};
    `
    : `
      background: white;
      color: #64748b;
      border: 1px solid #e2e8f0;
      &:hover { background: #f8fafc; color: #334155; }
    `
  }
`;

const TypeCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  border-radius: 10px;
  font-size: 10.5px;
  font-weight: 600;
  background: ${({ $active }) => $active ? "rgba(0,0,0,0.08)" : "#f1f5f9"};
  color: inherit;
`;

const TableCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
`;

const TableScroll = styled.div`
  overflow-x: auto;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: 'Geist', sans-serif;
  min-width: 1100px;
`;

const Th = styled.th`
  padding: 11px 14px;
  text-align: left;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #94a3b8;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  user-select: none;
  cursor: ${({ sortable }) => sortable ? "pointer" : "default"};
  &:hover { color: ${({ sortable }) => sortable ? "#475569" : "#94a3b8"}; }
`;

const SortIndicator = styled.span`
  opacity: ${({ $active }) => $active ? 1 : 0.3};
  color: ${({ $active }) => $active ? "#2563eb" : "inherit"};
  margin-left: 3px;
  font-size: 11px;
`;

const TableRow = styled.tr`
  animation: ${fadeUp} 0.3s ease both;
  animation-delay: ${({ $idx }) => Math.min($idx * 0.025, 0.4)}s;
  &:hover td { background: #f8fafc !important; }
  &:not(:last-child) td { border-bottom: 0.5px solid #f1f5f9; }
`;

const Td = styled.td`
  padding: 11px 14px;
  vertical-align: middle;
  color: ${({ $muted }) => $muted ? "#94a3b8" : "#334155"};
  font-family: ${({ $mono }) => $mono ? "'Geist Mono', monospace" : "'Geist', sans-serif"};
  font-size: ${({ $mono }) => $mono ? "12px" : "13px"};
  white-space: nowrap;
`;

const AgentCell = styled.div`display: flex; align-items: center; gap: 8px;`;

const Avatar = styled.div`
  width: 26px; height: 26px;
  border-radius: 7px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AgentName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
`;

const FolderTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: #f1f5f9;
  border: 0.5px solid #e2e8f0;
  border-radius: 5px;
  font-family: 'Geist Mono', monospace;
  font-size: 11.5px;
  color: #475569;
`;

const AmountGBP = styled.span`
  font-family: 'Geist Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  color: #dc2626;
`;

const BankBadge = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $border }) => $border};
`;

const ReasonBadge = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fca5a5;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemarksText = styled.span`
  display: inline-block;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #94a3b8;
  font-size: 12.5px;
`;

const EditBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px; height: 30px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #fff1f2; color: #dc2626; border-color: #fecaca; }
`;

const EmptyState = styled.div`text-align: center; padding: 64px 24px;`;
const EmptyIcon  = styled.div`font-size: 36px; margin-bottom: 12px; opacity: 0.2;`;
const EmptyTitle = styled.div`font-size: 15px; font-weight: 600; color: #64748b; margin-bottom: 4px;`;
const EmptyDesc  = styled.div`font-size: 13px; color: #94a3b8;`;

const TableFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
`;

const FooterLeft   = styled.div`font-size: 12px; color: #94a3b8; font-family: 'Geist Mono', monospace;`;
const FooterStrong = styled.strong`color: #64748b; font-weight: 500;`;
const FooterRight  = styled.div`font-size: 12px; color: #94a3b8; font-family: 'Geist Mono', monospace;`;
const FooterTotal  = styled.span`font-weight: 600; color: #dc2626; font-size: 14px; margin-left: 4px;`;

// ── Modal ──

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100vw; height: 100vh;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  animation: ${fadeIn} 0.18s ease both;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const Modal = styled.div`
  background: white;
  border-radius: 18px;
  width: min(860px, 94vw);
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  animation: ${scaleIn} 0.25s ease both;
  display: flex;
  flex-direction: column;
`;

const ModalHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 28px 20px;
  border-bottom: 1px solid #f1f5f9;
`;

const ModalEyebrow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 4px;
`;

const ModalTitle = styled.div`
  font-family: 'Instrument Serif', serif;
  font-size: 22px;
  font-weight: 400;
  color: #0f172a;
`;

const CloseBtn = styled.button`
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover { background: #f1f5f9; }
`;

const ModalBody = styled.div`padding: 24px 28px; flex: 1;`;

const NoCodeNote = styled.div`
  margin-top: 4px;
  padding: 14px 16px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  font-size: 12.5px;
  color: #1e40af;
  font-family: 'Geist', sans-serif;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 28px 24px;
  border-top: 1px solid #f1f5f9;
`;

const CancelModalBtn = styled.button`
  padding: 10px 20px;
  font-size: 13.5px;
  font-weight: 500;
  font-family: 'Geist', sans-serif;
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #f8fafc; }
`;

const UpdateBtn = styled.button`
  padding: 10px 24px;
  font-size: 13.5px;
  font-weight: 600;
  font-family: 'Geist', sans-serif;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #b91c1c; }
`;